import React, { useContext, useState, useRef, useLayoutEffect } from 'react';
import _merge from 'lodash/merge';
import classnames from 'classnames';
import { SPACING_8 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import { ListContext } from 'components/List';
import { CONSTANTS } from 'common/ListKeyboardNav';

import Tooltip from 'components/Tooltip/Tooltip';

import {
  StepTitle,
  StepDetail,
  StepTextContainer,
  StepLine,
  StepFormContext,
} from 'components/StepForm';

import {
  getIndicatorForVariant,
  getPressedColor,
  isSelectionKeyPressed,
} from './common/functions';

import DeprecatedRippleEffect, { DeprecatedRippleEffectHandle } from '../RippleEffect/DeprecatedRippleEffect';

import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;

const STEP_STYLE = { display: 'inline-block' };

export interface HorizontalStepContextInterface {
  disabled?: boolean;
  variant?: 'current' | 'future' | 'error' | 'warning' | 'completed';
  isOpen?: boolean;
  isLinear?: boolean;
  isPressed?: boolean;
}

const HorizontalStepContext = React.createContext<HorizontalStepContextInterface | null>({});

export interface HorizontalStepProps extends React.ComponentPropsWithoutRef<'li'> {
  /** Override the default indicator used for the HorizontalStep variant. Consider using one of the step indicator components (StepIndicator, StepErrorIndicator, StepWarningIndicator, StepCompletedIndicator). */
  indicator?: React.ReactNode;
  /** Apply properties to the default indicator. */
  indicatorProps?: {};
  /** HorizontalStep number for any linear step indicator that uses it. Only applies when using default indicator. */
  stepNumber?: number | string;
  /** The title for the step. */
  title: string;
  /** The extra details for the step. */
  details?: string;
  /** The current selected step. Usually it is the variant=current step, unless it has error or warning. */
  selected?: boolean;
  /** The variant for styling purposes.  */
  variant?: 'current' | 'future' | 'error' | 'warning' | 'completed';
  /* If true, the step is disabled. */
  disabled?: boolean;
  /** If `true`, the button will be rendered without ripple effect. */
  disabledRipple?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Properties for the ripple style. */
  rippleStyle?: {};
  /** Props to pass to the internal HorizontalStep element. */
  internalStepProps?: Partial<React.ComponentPropsWithRef<'div'>>;
  /** If true, disable any tooltip for details text. */
  disableTooltip?: boolean;
  /** Override tooltip properties for details text.  */
  tooltipProps?: {};
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** `HorizontalStep`, all other props will be provided to the root element */
const HorizontalStep = React.forwardRef<HTMLElement, HorizontalStepProps>((props: HorizontalStepProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    indicator,
    indicatorProps,
    stepNumber,
    title,
    details,
    disableTooltip,
    tooltipProps,
    selected,
    variant,
    disabled,
    theme: themeProp,
    disabledRipple,
    disableAnimation: propsdisableAnimation,
    rippleStyle,
    internalStepProps,
    ...otherProps
  } = props;
  const { style: propsStyle, ...restOfOtherProps } = otherProps;
  const [isPressed, setIsPressed] = useState(false);
  const [enableTextTooltip, setEnableTextTooltip] = useState(undefined);
  const tooltipText = useRef(undefined);
  const rippleRef = useRef<DeprecatedRippleEffectHandle>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef(null);
  const combinedStepRefs = useCombinedRefs(stepRef, ref);
  const stepFormContext = useContext(StepFormContext);
  const listContext = useContext(ListContext);

  const { ref: wrapperPropsRef, ...otherInternalStepProps } = internalStepProps;
  const combinedRefs = useCombinedRefs(wrapperRef, wrapperPropsRef);
  const resolvedTheme = _merge({}, theme.STEP_FORM, themeProp && themeProp.STEP_FORM ? themeProp.STEP_FORM : {});
  const [pressedColor, setPressedColor] = useState(resolvedTheme.pressed.background);
  const isLinear = (stepNumber !== undefined);
  const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);

  const TOOLTIP_PROPS = {
    placement: 'bottom' as const,
    trigger: ['hover' as const, 'focus' as const],
    tooltip: tooltipText.current || '' as const,
    triggerRer: ref,
    modifiers: { offset: { offset: '0, 10' } },
    disable: !enableTextTooltip || disableTooltip,
    ...tooltipProps
  };

  useLayoutEffect(() => {
    if (variant === 'current' && !disabled) {
      stepFormContext && stepFormContext.setSelectedRef && stepFormContext.setSelectedRef(stepRef.current);
    }
  }, [props.selected, disabled, variant]);

  const handleEnableTooltip = (clampedObj) => {
    setEnableTextTooltip(clampedObj.clamped);
    tooltipText.current = undefined;
    if (clampedObj.clamped) {
      tooltipText.current = clampedObj.fullText;
    }
  };

  const handleOnKeyDown = (event) => {
    const { onKeyDown } = otherProps;
    if (isSelectionKeyPressed(event.key) && !disabled) {
      setPressedColor(getPressedColor(resolvedTheme, variant, false));
      setIsPressed(true);
      rippleRef.current.handleKeyDown(event);
    }
    onKeyDown && onKeyDown(event);
  };

  const handleOnKeyUp = (event) => {
    const { onKeyUp } = otherProps;
    !disabled && setIsPressed(false);
    onKeyUp && onKeyUp(event);
  };

  const handleClick = (event) => {
    const { onClick } = otherProps;
    event.preventDefault();
    if (!disabled) {
      stepFormContext.hoverEffect && combinedRefs && combinedRefs.current && combinedRefs.current.focus();
      setPressedColor(getPressedColor(resolvedTheme, variant, true));
    }
    onClick && onClick(event);
  };

  const handleMouseDown = (event) => {
    const { onMouseDown } = otherProps;
    !disabled && setIsPressed(true);
    onMouseDown && onMouseDown(event);
  };
  const handleMouseUp = (event) => {
    const { onMouseUp } = otherProps;
    !disabled && setIsPressed(false);
    onMouseUp && onMouseUp(event);
  };
  const handleMouseLeave = (event) => {
    const { onMouseLeave } = otherProps;
    !disabled && setIsPressed(false);
    onMouseLeave && onMouseLeave(event);
  };
  const handleBlur = (event) => {
    const { onBlur } = otherProps;
    !disabled && setIsPressed(false);
    onBlur && onBlur(event);
  };

  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${listContext.uniqueListId}`]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });

  const content = (
    <>
      <Styled.StepWrapper
        as="li"
        ref={combinedStepRefs}
        rtl={RTL}
        style={{
          ...STEP_STYLE,
          ...propsStyle,
        }}
        disabled={disabled}
        $theme={resolvedTheme}
        $variant={variant}
        aria-current={selected}
        $hasDetails={details !== undefined}
        $isHorizontal
        $hoverEffect={stepFormContext.hoverEffect}
        {...restOfOtherProps}
        onKeyDown={handleOnKeyDown}
        onKeyUp={handleOnKeyUp}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onBlur={handleBlur}
      >
        <DeprecatedRippleEffect
          as="div"
          ref={rippleRef}
          disabled={disabledRipple || disabled || resolvedDisableAnimation}
          style={{ height: '100%', width: '100%' }}
          rippleStyle={{ backgroundColor: pressedColor, ...rippleStyle }}
        >
          <Styled.HorizontalStep
            className={classes}
            ref={combinedRefs}
            tabIndex={-1}
            rtl={RTL}
            $theme={resolvedTheme}
            $variant={variant}
            $hasDetails={details !== undefined}
            onClick={(event) => {
              event.preventDefault();
            }}
            {...otherInternalStepProps}
          >
            <HorizontalStepContext.Provider
              value={{ disabled, variant, isOpen: false, isLinear, isPressed }}
            >
              <Styled.StepIndicatorContainer
                $theme={resolvedTheme}
                $variant={variant}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: SPACING_8,
                }}
              >
                {indicator || getIndicatorForVariant(variant, stepNumber, resolvedTheme, indicatorProps)}
                <StepLine $numberOfLines={0} />
              </Styled.StepIndicatorContainer>
              <StepTextContainer>
                <Tooltip {...TOOLTIP_PROPS}>
                  <StepTitle theme={resolvedTheme.TITLE} isPressed={isPressed} disabled={disabled} variant={variant} isLinear={isLinear}>{title}</StepTitle>
                  {details && (
                    <StepDetail
                      theme={resolvedTheme.DETAIL}
                      isPressed={isPressed}
                      enableTooltip={handleEnableTooltip}
                    >
                      {details}
                    </StepDetail>
                  )}
                </Tooltip>
              </StepTextContainer>
            </HorizontalStepContext.Provider>
          </Styled.HorizontalStep>
        </DeprecatedRippleEffect>
      </Styled.StepWrapper>
    </>
  );

  return content;
}));

HorizontalStep.defaultProps = {
  indicator: undefined,
  indicatorProps: {},
  stepNumber: undefined,
  details: undefined,
  selected: false,
  variant: 'future',
  disabled: false,
  theme: {},
  disabledRipple: undefined,
  disableAnimation: undefined,
  rippleStyle: {},
  internalStepProps: {},
  disableTooltip: true,
  tooltipProps: {},
};

HorizontalStep.displayName = 'HorizontalStep';

export { HorizontalStep as default, HorizontalStepContext };
